import React, { useState } from "react";
import { Relationship } from "~/types/storage";
import { generateUUID } from "~/utils/uuid";
import { Button } from "~/widgets/Button/Button";
import GlossaryMarkdown from "~/widgets/GlossaryMarkdown/GlossaryMarkdown";
import styles from "~/pages/CharacterSheet/widgets/RelationshipsSection/RelationshipsSection.scss";

export interface RelationshipsSectionProps {
  relationships: Relationship[];
  onRelationshipsChange: (relationships: Relationship[]) => void;
}

const RelationshipsSection: React.FC<RelationshipsSectionProps> = ({
  relationships,
  onRelationshipsChange,
}) => {
  const [editingId, setEditingId] = useState<string | null>(null);

  const addRelationship = () => {
    const newRelationship: Relationship = {
      id: generateUUID(),
      name: "",
      content: "",
    };
    onRelationshipsChange([...relationships, newRelationship]);
    setEditingId(newRelationship.id);
  };

  const updateRelationship = (id: string, updates: Partial<Relationship>) => {
    onRelationshipsChange(
      relationships.map((rel) =>
        rel.id === id ? { ...rel, ...updates } : rel
      )
    );
  };

  const deleteRelationship = (id: string) => {
    onRelationshipsChange(relationships.filter((rel) => rel.id !== id));
    if (editingId === id) {
      setEditingId(null);
    }
  };

  return (
    <section className={styles.relationshipsSection}>
      <div className={styles.sectionHeader}>
        <h3 className={styles.sectionTitle}>Relationships</h3>
      </div>

      <div className={styles.relationshipsList}>
        {relationships.length === 0 ? (
          <div className={styles.emptyState}>
            No relationships yet. Add one to track connections with NPCs and other characters.
          </div>
        ) : (
          relationships.map((rel) => {
            const isEditing = editingId === rel.id;

            if (isEditing) {
              const handleExitEdit = (e: React.KeyboardEvent) => {
                e.stopPropagation();
                if ((e.key === "Enter" && (e.metaKey || e.ctrlKey)) || e.key === "Escape") {
                  e.preventDefault();
                  setEditingId(null);
                }
              };

              return (
                <div
                  key={rel.id}
                  className={`${styles.relationshipCard} ${styles.editing}`}
                >
                  <div className={styles.relationshipHeader}>
                    <input
                      type="text"
                      className={styles.relationshipNameInput}
                      value={rel.name}
                      onChange={(e) =>
                        updateRelationship(rel.id, { name: e.target.value })
                      }
                      placeholder="Name (e.g., Jaune Arc)"
                      autoFocus
                      onKeyDown={handleExitEdit}
                    />
                  </div>
                  <textarea
                    className={styles.relationshipTextarea}
                    value={rel.content}
                    onChange={(e) =>
                      updateRelationship(rel.id, { content: e.target.value })
                    }
                    placeholder="Describe the relationship..."
                    onKeyDown={handleExitEdit}
                  />
                  <div className={styles.editHint}>
                    <strong>CMD/CTRL+Enter</strong> or <strong>ESC</strong> to save
                  </div>
                  <div className={styles.relationshipActions}>
                    <Button
                      type="button"
                      onClick={() => setEditingId(null)}
                      variant="primary"
                      size="sm"
                    >
                      Done
                    </Button>
                    <Button
                      type="button"
                      onClick={() => deleteRelationship(rel.id)}
                      variant="danger"
                      size="sm"
                    >
                      Delete
                    </Button>
                  </div>
                </div>
              );
            }

            return (
              <div
                key={rel.id}
                className={styles.relationshipCard}
                tabIndex={0}
                onDoubleClick={() => setEditingId(rel.id)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    setEditingId(rel.id);
                  }
                }}
              >
                <div className={styles.relationshipHeader}>
                  <span className={styles.relationshipName}>
                    {rel.name || <em style={{ color: "var(--colorTextMuted)" }}>Unnamed</em>}
                  </span>
                  <button
                    type="button"
                    className={styles.editToggle}
                    onClick={() => setEditingId(rel.id)}
                  >
                    Edit
                  </button>
                </div>
                <div className={styles.relationshipContent}>
                  {rel.content ? (
                    <GlossaryMarkdown>{rel.content}</GlossaryMarkdown>
                  ) : (
                    <span style={{ color: "var(--colorTextMuted)", fontStyle: "italic" }}>
                      No details. Double-click to edit.
                    </span>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>

      <button
        type="button"
        className={styles.addRelationshipBtn}
        onClick={addRelationship}
      >
        + Add Relationship
      </button>
    </section>
  );
};

export default RelationshipsSection;
